import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_API_URL || 'https://back.shoplycircle.com';
const EXPECTED_ROLE = 'manufacturer';

let csrfToken: string | null = null;

function getCsrfToken(): string | null {
  // First try to get from localStorage
  const stored = localStorage.getItem('csrf_token');
  if (stored) return stored;
  
  // Then try to get from cookie
  const name = 'XSRF-TOKEN';
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

async function fetchCsrfToken() {
  try {
    await axios.get(`${API_URL}/sanctum/csrf-cookie`, {
      withCredentials: true,
    });
    csrfToken = getCsrfToken();
    return csrfToken;
  } catch (err) {
    console.error('Failed to fetch CSRF token:', err);
    return null;
  }
}

// Initialize CSRF token on app load
fetchCsrfToken();

function clearAuthAndRedirect(to: string = '/login') {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('csrf_token');
  window.location.href = to;
}

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');

  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (user?.role !== EXPECTED_ROLE) {
        clearAuthAndRedirect('/login?error=role_mismatch');
        return Promise.reject(new Error('Role mismatch for manufacturer dashboard'));
      }
    } catch {
      clearAuthAndRedirect('/login?error=invalid_session');
      return Promise.reject(new Error('Invalid user session'));
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add CSRF token to headers for state-changing requests
  const csrf = getCsrfToken();
  if (csrf && ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')) {
    config.headers['X-CSRF-TOKEN'] = csrf;
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle 419 CSRF token expired - refetch token and retry
    if (error.response?.status === 419) {
      await fetchCsrfToken();
      if (error.config) {
        const csrf = getCsrfToken();
        if (csrf) {
          error.config.headers['X-CSRF-TOKEN'] = csrf;
        }
        return axios(error.config);
      }
    }

    // Don't redirect on login/register endpoints - let them handle their own errors
    const isAuthEndpoint = error.config?.url?.includes('/login') || error.config?.url?.includes('/register');
    
    if (error.response?.status === 401 && !isAuthEndpoint) {
      clearAuthAndRedirect('/login?error=session_expired');
    }

    if (error.response?.status === 403) {
      const message = (error.response?.data?.message || '').toString().toLowerCase();
      if (message.includes('required role') || message.includes('invalid credentials for this portal')) {
        clearAuthAndRedirect('/login?error=forbidden_role');
      }
    }

    // Show error toast for all other errors
    if (!isAuthEndpoint && error.response?.status !== 419) {
      const errMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        (typeof error.response?.data === 'string' ? error.response.data : null) ||
        error.message ||
        'An unexpected error occurred';
      toast.error(errMsg);
    }

    return Promise.reject(error);
  }
);

export const resolveImageUrl = (path?: string | null): string => {
  if (!path) return '';
  if (path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:') || path.startsWith('/placeholder')) return path;
  const API_URL = process.env.REACT_APP_API_URL || 'https://back.shoplycircle.com';
  return `${API_URL}/storage/${path.replace(/^\/?storage\//, '')}`;
};

/**
 * Get the best available image URL for a product.
 * Prioritizes main_image_url, then falls back to the first image in the images array.
 */
export function getProductImage(product?: {
  main_image_url?: string | null;
  images?: Array<{ url?: string | null; image_url?: string | null }>;
} | null): string {
  if (!product) return '';
  const raw =
    product.main_image_url ||
    product.images?.[0]?.url ||
    product.images?.[0]?.image_url ||
    null;
  return resolveImageUrl(raw);
}

export default api;
